/**
 * resolveAdapter - Adapter resolution (no fallback in real mode).
 *
 * Mode behavior:
 * - VITE_API_MODE === "mock" → MockAdapter (for local dev)
 * - VITE_API_MODE === "real" → RealAdapter directly (no fallback to mock)
 *
 * In real mode, if a RealAdapter method is not implemented it will throw
 * REAL_NOT_IMPLEMENTED which surfaces as a visible, deterministic failure
 * through the standard error path.
 *
 * Pages and hooks call getAdapter() to get the resolved adapter instance.
 */

import { env } from "@/lib/env"
import type { IDataAdapter } from "./IDataAdapter"
import { MockAdapter } from "./MockAdapter"
import { RealAdapter } from "./RealAdapter"

/**
 * API mode from environment configuration.
 */
const apiMode = env.VITE_API_MODE

/**
 * Singleton adapter instance.
 */
let adapterInstance: IDataAdapter | null = null

/**
 * Returns the resolved adapter instance.
 *
 * This is the ONLY entry point for data operations.
 * Pages and hooks should call this function to get the adapter.
 */
export function getAdapter(): IDataAdapter {
  if (!adapterInstance) {
    adapterInstance = apiMode === "mock" ? MockAdapter : RealAdapter
  }
  return adapterInstance
}

/**
 * Resets the adapter instance (for testing purposes only).
 */
export function resetAdapter(): void {
  adapterInstance = null
}

// Re-export types and utilities for consumer convenience
export type { IDataAdapter } from "./IDataAdapter"
export {
  isAdapterError,
  isRealNotImplementedError,
} from "./IDataAdapter"
